/**
 * FormErrorSummary — accessible, focusable list of a form's validation errors.
 *
 * Renders nothing when there are no errors. When errors are present it shows an
 * `role="alert"` summary so screen readers announce it, and each entry is a
 * button that moves focus to the offending field. Pair with react-hook-form's
 * `shouldFocusError` (focuses the first invalid field on submit) for a complete
 * "tell me what's wrong and take me there" experience (WCAG 3.3.1).
 */

import React from "react";
import { Alert, AlertTitle, Link, List, ListItem } from "@mui/material";
import type { FieldErrors } from "react-hook-form";

interface FlatError {
  name: string;
  message: string;
}

/** Walk RHF's nested FieldErrors into a flat [{name, message}] list. */
function flattenErrors(errors: FieldErrors, prefix = ""): FlatError[] {
  const out: FlatError[] = [];
  for (const key of Object.keys(errors)) {
    const node = (errors as Record<string, unknown>)[key];
    if (!node || typeof node !== "object") continue;
    const path = prefix ? `${prefix}.${key}` : key;
    const message = (node as { message?: unknown }).message;
    if (typeof message === "string" && message) {
      out.push({ name: path, message });
    } else {
      // Nested group / field-array entry — recurse.
      out.push(...flattenErrors(node as FieldErrors, path));
    }
  }
  return out;
}

export interface FormErrorSummaryProps {
  errors: FieldErrors;
  /** Optional human labels keyed by field name (falls back to the field name). */
  fieldLabels?: Record<string, string>;
  title?: string;
}

const focusField = (name: string) => {
  const el = document.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"]`);
  if (el) {
    el.focus();
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }
};

export const FormErrorSummary: React.FC<FormErrorSummaryProps> = ({
  errors,
  fieldLabels = {},
  title,
}) => {
  const alertRef = React.useRef<HTMLDivElement>(null);
  const flat = React.useMemo(() => flattenErrors(errors), [errors]);

  // Scroll the summary into view when it (re)appears after a failed submit.
  React.useEffect(() => {
    if (flat.length > 0) {
      alertRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [flat.length]);

  if (flat.length === 0) return null;

  const heading =
    title ??
    `Please fix ${flat.length} ${flat.length === 1 ? "error" : "errors"} below`;

  return (
    <Alert ref={alertRef} severity="error" role="alert" sx={{ mb: 3 }}>
      <AlertTitle>{heading}</AlertTitle>
      <List dense disablePadding>
        {flat.map((err) => (
          <ListItem key={err.name} disableGutters sx={{ py: 0.25 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              color="error"
              onClick={() => focusField(err.name)}
              sx={{ textAlign: "left" }}
            >
              {fieldLabels[err.name] ? `${fieldLabels[err.name]}: ` : ""}
              {err.message}
            </Link>
          </ListItem>
        ))}
      </List>
    </Alert>
  );
};

export default FormErrorSummary;
